import { useParams, Link } from "react-router-dom";
import { Layout } from "@jambushrushti/components/layout/Layout";
import { PageBreadcrumb } from "@jambushrushti/components/ui/page-breadcrumb";
import { Button } from "@jambushrushti/components/ui/button";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import {
  ArrowLeft,
  BookOpen,
  Scroll as ScrollIcon,
  User,
  Calendar,
  MapPin,
  Globe,
  Info,
  Mail,
  FileText,
} from "lucide-react";
import { useLibraryCatalog } from "@jambushrushti/hooks/useLibraryCatalog";

const LibraryItemDetails = () => {
  const {
    data: catalogItems = [],
    isLoading,
    error,
    refetch,
  } = useLibraryCatalog();
  const { id } = useParams();

  const item = catalogItems.find(i => i.id === id);

  // Get similar items (same type or category, excluding current)
  const similarItems = item
    ? catalogItems
        .filter(
          i =>
            (i.type === item.type || i.category === item.category) &&
            i.id !== item.id
        )
        .slice(0, 4)
    : [];

  if (isLoading || error) {
    return (
      <Layout>
        <div
          className="space-y-4 py-20 text-center"
          role={error ? "alert" : "status"}
        >
          <p>{error ? error.message : "Loading book..."}</p>
          {error && (
            <Button variant="outline" onClick={() => refetch()}>
              Retry
            </Button>
          )}
        </div>
      </Layout>
    );
  }

  if (!item) {
    return (
      <Layout>
        <section className="bg-background py-20">
          <div className="container mx-auto px-4 text-center">
            <BookOpen className="mx-auto mb-4 h-16 w-16 text-muted-foreground/30" />
            <h1 className="font-display mb-4 text-2xl font-bold text-foreground">
              Item Not Found
            </h1>
            <p className="mb-6 text-muted-foreground">
              The item you're looking for doesn't exist or has been removed.
            </p>
            <Button variant="hero" asChild>
              <Link to="/library">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Library
              </Link>
            </Button>
          </div>
        </section>
      </Layout>
    );
  }

  const ItemIcon = item.type === "Book" ? BookOpen : ScrollIcon;
  const isManuscript = item.type === "Manuscript";

  return (
    <Layout>
      {/* Breadcrumb */}
      <PageBreadcrumb
        items={[{ label: "Library", href: "/library" }, { label: item.title }]}
      />

      <section className="bg-gradient-to-b from-secondary/30 via-background to-background py-8 sm:py-12">
        <div className="container mx-auto px-4">
          <div className="mx-auto grid max-w-6xl items-start gap-8 lg:grid-cols-[360px_1fr] lg:gap-12">
            <aside className="lg:sticky lg:top-24">
              <div className="rounded-2xl border bg-card p-3 shadow-lg shadow-black/5">
                <div className="aspect-[3/4] overflow-hidden rounded-xl bg-secondary/50">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.title}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center">
                      <ItemIcon className="h-20 w-20 text-primary/25" />
                    </div>
                  )}
                </div>
                <div className="mt-3 flex items-center justify-between gap-3 px-1">
                  <Badge variant="outline">
                    <ItemIcon className="mr-1.5 h-3.5 w-3.5" />
                    {item.type}
                  </Badge>
                  <Badge
                    className={
                      item.available
                        ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                        : "bg-muted text-muted-foreground"
                    }
                  >
                    {item.available === null
                      ? "Not specified"
                      : item.available
                        ? "Available"
                        : "Reference only"}
                  </Badge>
                </div>
              </div>
            </aside>

            <div className="animate-fade-up space-y-5">
              <div className="rounded-2xl border bg-card p-5 shadow-sm sm:p-7">
                <div className="mb-5 flex flex-wrap items-center gap-2">
                  {item.category && <Badge>{item.category}</Badge>}
                  {item.language && (
                    <Badge variant="secondary">{item.language}</Badge>
                  )}
                  <span className="ml-auto text-xs text-muted-foreground">
                    Catalog ID: {item.id}
                  </span>
                </div>

                <h1 className="font-display text-3xl font-bold leading-tight text-foreground sm:text-4xl">
                  {item.title}
                </h1>
                <p className="mt-2 flex items-center gap-2 text-base text-muted-foreground">
                  <User className="h-4 w-4 text-primary" />
                  {item.author || "Author not specified"}
                </p>

                <div className="my-6 grid gap-3 sm:grid-cols-2">
                  <div className="flex items-center gap-3 rounded-xl bg-secondary/40 p-3">
                    <Calendar className="h-5 w-5 shrink-0 text-primary" />
                    <span className="text-sm">
                      <span className="block text-xs text-muted-foreground">
                        Year
                      </span>
                      <span className="font-medium text-foreground">
                        {item.year || "Not specified"}
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-3 rounded-xl bg-secondary/40 p-3">
                    <MapPin className="h-5 w-5 shrink-0 text-primary" />
                    <span className="text-sm">
                      <span className="block text-xs text-muted-foreground">
                        Location
                      </span>
                      <span className="font-medium text-foreground">
                        {item.location || "Not specified"}
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-3 rounded-xl bg-secondary/40 p-3">
                    <Globe className="h-5 w-5 shrink-0 text-primary" />
                    <span className="text-sm">
                      <span className="block text-xs text-muted-foreground">
                        Publisher
                      </span>
                      <span className="font-medium text-foreground">
                        {item.publisher || "Not specified"}
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-3 rounded-xl bg-secondary/40 p-3">
                    <FileText className="h-5 w-5 shrink-0 text-primary" />
                    <span className="text-sm">
                      <span className="block text-xs text-muted-foreground">
                        Pages
                      </span>
                      <span className="font-medium text-foreground">
                        {item.pages ?? "Not specified"}
                      </span>
                    </span>
                  </div>
                </div>

                {item.description && (
                  <div className="border-t pt-5">
                    <h2 className="font-display mb-2 text-lg font-semibold text-foreground">
                      About this item
                    </h2>
                    <p className="leading-7 text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                )}

                {isManuscript && (
                  <div className="mt-5 border-t pt-5">
                    <h2 className="font-display mb-3 text-lg font-semibold">
                      Manuscript details
                    </h2>
                    <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
                      {item.granthName && (
                        <div>
                          <dt className="text-muted-foreground">Granth name</dt>
                          <dd className="font-medium">{item.granthName}</dd>
                        </div>
                      )}
                      {item.hastrapNo && (
                        <div>
                          <dt className="text-muted-foreground">Hastrap no.</dt>
                          <dd className="font-medium">{item.hastrapNo}</dd>
                        </div>
                      )}
                      {item.mulkarta && (
                        <div>
                          <dt className="text-muted-foreground">Mulkarta</dt>
                          <dd className="font-medium">{item.mulkarta}</dd>
                        </div>
                      )}
                      {item.tikakarta && (
                        <div>
                          <dt className="text-muted-foreground">Tikakarta</dt>
                          <dd className="font-medium">{item.tikakarta}</dd>
                        </div>
                      )}
                      {item.folioNo && (
                        <div>
                          <dt className="text-muted-foreground">Folio no.</dt>
                          <dd className="font-medium">{item.folioNo}</dd>
                        </div>
                      )}
                      {item.totalPages && item.availablePages && (
                        <div>
                          <dt className="text-muted-foreground">
                            Available pages
                          </dt>
                          <dd className="font-medium">
                            {item.availablePages} / {item.totalPages}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                {item.pdf_url && (
                  <Button asChild variant="hero" size="lg">
                    <a
                      href={item.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FileText className="mr-2 h-4 w-4" /> Read PDF
                    </a>
                  </Button>
                )}
                <Button
                  variant={item.pdf_url ? "outline" : "hero"}
                  size="lg"
                  asChild
                >
                  <Link to="/contact">
                    <Mail className="mr-2 h-4 w-4" />
                    Contact Librarian
                  </Link>
                </Button>
              </div>

              {item.audio_url && (
                <audio
                  controls
                  preload="none"
                  src={item.audio_url}
                  className="w-full"
                  aria-label={`${item.title} audio`}
                />
              )}
              <div className="flex items-start gap-3 rounded-xl border border-primary/15 bg-primary/5 p-4">
                <Info className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <p className="text-sm leading-6 text-muted-foreground">
                  {isManuscript
                    ? "This manuscript is part of the special collection. Contact the librarian to request supervised access."
                    : "Contact the librarian to confirm physical availability or request access."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Similar Items */}
      {similarItems.length > 0 && (
        <section className="bg-gradient-spiritual relative overflow-hidden py-16">
          <div className="lotus-pattern absolute inset-0 opacity-30" />

          <div className="container relative z-10 mx-auto px-4">
            <div className="mb-10 text-center">
              <span className="text-xs font-medium uppercase tracking-widest text-primary">
                Explore More
              </span>
              <h2 className="font-display mt-2 text-2xl font-bold text-foreground lg:text-3xl">
                Similar Items
              </h2>
            </div>

            <div className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {similarItems.map((similarItem, index) => {
                const SimilarIcon =
                  similarItem.type === "Book" ? BookOpen : ScrollIcon;
                return (
                  <Link
                    key={similarItem.id}
                    to={`/library/${similarItem.id}`}
                    className="group"
                  >
                    <Card
                      variant="interactive"
                      className="animate-fade-up h-full overflow-hidden bg-card"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div className="aspect-[3/4] overflow-hidden bg-secondary/30">
                        <img
                          src={similarItem.image}
                          alt={similarItem.title}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <CardContent className="p-4">
                        <div className="mb-2 flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            <SimilarIcon className="mr-1 h-3 w-3" />
                            {similarItem.type}
                          </Badge>
                        </div>
                        <h3 className="font-display line-clamp-2 text-sm font-medium text-foreground transition-colors group-hover:text-primary">
                          {similarItem.title}
                        </h3>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {similarItem.author}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default LibraryItemDetails;
